import React, { useEffect, useState } from 'react';
import { DailyReward } from '../types';

interface DailyRewardModalProps {
  isOpen: boolean;
  reward: DailyReward;
  onClaim: () => void;
}

// Motivational quotes from The System
const SYSTEM_QUOTES = [
  "Your dedication has been rewarded. Continue your training, Hunter.",
  "Every login brings you closer to S-Rank. Never give up.",
  "The path to power is paved with consistency. Well done, Hunter.",
  "Only those who persist can rise from E-Rank to greatness.",
  "Your determination fuels your evolution. Keep pushing forward.",
  "The System recognizes your commitment. Your transformation has begun.",
  "Strength is built one day at a time. You're becoming stronger.",
  "Each streak is a testament to your will. The System is watching.",
  "True hunters never miss a day. Your resolve is admirable.",
  "The gap between you and S-Rank shrinks with every login. Continue.",
  "Consistency is the weapon of champions. You're on the right path.",
  "The System rewards those who show up. Your efforts will not be forgotten.",
];

const DailyRewardModal: React.FC<DailyRewardModalProps> = ({
  isOpen,
  reward,
  onClaim,
}) => {
  const [showFlash, setShowFlash] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [animateRewards, setAnimateRewards] = useState(false);
  const [systemQuote, setSystemQuote] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Select random quote
      const randomQuote = SYSTEM_QUOTES[Math.floor(Math.random() * SYSTEM_QUOTES.length)];
      setSystemQuote(randomQuote);
      
      // Flash effect
      setShowFlash(true);
      setTimeout(() => setShowFlash(false), 300);
      
      // Show content after flash
      setTimeout(() => setShowContent(true), 350);
      
      // Animate rewards sequentially
      setTimeout(() => setAnimateRewards(true), 700);
    } else {
      setShowContent(false);
      setAnimateRewards(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Flash Effect */}
      {showFlash && (
        <div className="fixed inset-0 bg-blue-400 z-[9999] animate-fade-out pointer-events-none" />
      )}

      {/* Modal Overlay */}
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9998] flex items-center justify-center p-3 sm:p-4">
        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 30 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-blue-400 rounded-full animate-float-particle"
              style={{
                left: `${Math.random() * 100}%`,
                bottom: '-10px',
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>

        {/* Modal Content */}
        {showContent && (
          <div className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Glowing Background Orbs */}
            <div className="absolute inset-0 -z-10">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-glow-pulse" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-600/20 rounded-full blur-3xl animate-glow-pulse" style={{ animationDelay: '0.5s' }} />
            </div>

            {/* Main Modal Card */}
            <div className="bg-gradient-to-b from-blue-950/95 to-black/95 border-2 border-blue-500/50 rounded-lg shadow-[0_0_50px_rgba(59,130,246,0.5)] overflow-hidden">
              {/* Hexagonal Pattern Overlay */}
              <div 
                className="absolute inset-0 opacity-5 pointer-events-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l25.98 15v30L30 60 4.02 45V15z' fill='none' stroke='%23fff' stroke-width='1'/%3E%3C/svg%3E")`,
                  backgroundSize: '60px 60px',
                }}
              />

              {/* Top Border Glow */}
              <div className="h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-pulse" />

              {/* Content */}
              <div className="relative p-4 sm:p-8">
                {/* Title */}
                <div className="text-center mb-4 sm:mb-8">
                  <h2 className="text-2xl sm:text-5xl font-orbitron font-black text-blue-400 mb-1 sm:mb-2 tracking-wider drop-shadow-[0_0_10px_rgba(59,130,246,0.8)] uppercase animate-slide-in">
                    Daily Login Reward
                  </h2>
                  <div className="flex items-center justify-center gap-1.5 sm:gap-2 mt-2 sm:mt-4">
                    <span className="text-orange-400 text-2xl sm:text-3xl">🔥</span>
                    <p className="text-sm sm:text-lg font-rajdhani text-gray-300">
                      <span className="text-orange-400 font-bold text-lg sm:text-2xl">{reward.streakDay}</span> Day Streak!
                    </p>
                    <span className="text-orange-400 text-2xl sm:text-3xl">🔥</span>
                  </div>
                </div>

                {/* Base Rewards */}
                <div className={`grid grid-cols-2 gap-2 sm:gap-4 mb-4 sm:mb-6 transition-all duration-500 ${animateRewards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                  {/* XP Reward */}
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-lg p-3 sm:p-6 text-center transform hover:scale-105 transition-all">
                    <div className="text-2xl sm:text-4xl mb-1 sm:mb-2">⚡</div>
                    <div className="text-xl sm:text-3xl font-black font-orbitron text-blue-400 mb-0.5 sm:mb-1">
                      +{reward.xp}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-400 font-rajdhani uppercase">Experience</div>
                  </div>

                  {/* Currency Reward */}
                  <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border border-yellow-500/30 rounded-lg p-3 sm:p-6 text-center transform hover:scale-105 transition-all">
                    <div className="text-2xl sm:text-4xl mb-1 sm:mb-2">💰</div>
                    <div className="text-xl sm:text-3xl font-black font-orbitron text-yellow-400 mb-0.5 sm:mb-1">
                      +{reward.currency}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-400 font-rajdhani uppercase">Gold</div>
                  </div>
                </div>

                {/* Bonus Reward */}
                {reward.bonusReward && (
                  <div className={`mb-4 sm:mb-6 transition-all duration-500 delay-300 ${animateRewards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 border border-purple-500/50 rounded-lg p-3 sm:p-6">
                      <div className="flex items-center justify-center gap-2 sm:gap-4">
                        <div className="text-3xl sm:text-5xl animate-bounce">{reward.bonusReward.icon}</div>
                        <div className="text-center">
                          <div className="text-sm sm:text-lg font-orbitron font-bold text-purple-400 uppercase mb-0.5 sm:mb-1">
                            🎁 Bonus Reward!
                          </div>
                          <div className="text-lg sm:text-2xl font-black font-orbitron text-white">
                            {reward.bonusReward.name}
                          </div>
                          {reward.bonusReward.type === 'xp_boost' && (
                            <div className="text-blue-400 text-base sm:text-xl">+{reward.bonusReward.value as number} XP</div>
                          )}
                          {reward.bonusReward.type === 'currency_boost' && (
                            <div className="text-yellow-400 text-base sm:text-xl">+{reward.bonusReward.value as number} Gold</div>
                          )}
                          {reward.bonusReward.type === 'stat_boost' && (
                            <div className="text-green-400 text-base sm:text-xl">
                              {Object.entries(reward.bonusReward.value as object).map(([stat, val]) => (
                                <span key={stat}>+{val} {stat}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-3xl sm:text-5xl animate-bounce">{reward.bonusReward.icon}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Weekly Milestone Bonus */}
                {reward.weeklyBonus && (
                  <div className={`mb-4 sm:mb-6 transition-all duration-500 delay-500 ${animateRewards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-yellow-500/20 border-2 border-yellow-500/50 rounded-lg p-3 sm:p-6 animate-pulse">
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                        <div className="text-4xl sm:text-6xl">{reward.weeklyBonus.icon}</div>
                        <div className="text-center">
                          <div className="text-base sm:text-2xl font-orbitron font-black text-yellow-400 uppercase mb-1 sm:mb-2">
                            🎉 {reward.weeklyBonus.name} 🎉
                          </div>
                          <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-4">
                            <div className="text-blue-400 text-base sm:text-xl font-bold">+{reward.weeklyBonus.xp} XP</div>
                            <span className="text-gray-500 hidden sm:inline">•</span>
                            <div className="text-yellow-400 text-base sm:text-xl font-bold">+{reward.weeklyBonus.currency} Gold</div>
                          </div>
                        </div>
                        <div className="text-4xl sm:text-6xl hidden sm:block">{reward.weeklyBonus.icon}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* System Message */}
                <div className="bg-black/50 border border-blue-500/30 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                  <p className="text-center font-rajdhani text-gray-300 text-xs sm:text-sm italic">
                    "{systemQuote}"
                  </p>
                  <p className="text-center text-[10px] sm:text-xs text-gray-500 mt-1 sm:mt-2">— THE SYSTEM</p>
                </div>

                {/* Claim Button */}
                <button
                  onClick={onClaim}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white font-orbitron font-bold py-3 sm:py-4 px-4 sm:px-6 rounded uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] border border-blue-400/50 text-base sm:text-xl"
                >
                  Claim Rewards
                </button>
              </div>

              {/* Bottom Border Glow */}
              <div className="h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-pulse" />
            </div>

            {/* Pulsing Outer Glow */}
            <div className="absolute inset-0 border-2 border-blue-500/30 rounded-lg animate-pulse pointer-events-none" />
          </div>
        )}
      </div>
    </>
  );
};

export default DailyRewardModal;

